/**
 * BUG-109 — the draft editor's save state, as a pure reducer.
 *
 * Editing piece 1788's draft sent PATCH /drafts/958 roughly every 0.8 s with
 * nobody typing. A save made the editor read-only, TipTap's setEditable()
 * emitted an update, and the page compared the editor's HTML with the
 * server's sanitized copy — which the sanitizer re-serializes, so it never
 * equals editor.getHTML() — and saved again, forever.
 *
 * The rule here: the baseline is what was last SENT, in the editor's own
 * serialization, never the server's copy. A round trip through the server is
 * then not a change, even when the sanitizer drops something. While editing,
 * a refetch of the same draft is an echo of our own save and is ignored; a
 * new draft version (or a change while not editing) replaces everything.
 *
 * Done never offers to discard: it closes when everything is saved, saves the
 * last edit first when it isn't, and waits for a save already in flight. It
 * stays open only when that save fails.
 *
 * A failed save is not retried by itself: the first version of this fix
 * retried every ~0.5 s on any persistent error (403, 422, 500). The failed
 * text waits for the next edit, or for Done — a person asking.
 */

export interface DraftSaveState {
  /** What the server holds, as the editor serializes it. */
  baseline: string;
  /** What the editor shows now. */
  html: string;
  dirty: boolean;
  saving: boolean;
  /** The HTML of the save in flight; becomes the baseline if it succeeds. */
  sentHtml: string | null;
  /** Done was pressed and is waiting for a save to finish. */
  closeAfterSave: boolean;
  /** Everything is saved and Done was pressed: the editor should close. */
  readyToClose: boolean;
  /** The HTML whose save last failed; autosave waits for different text. */
  failedHtml: string | null;
}

export type DraftSaveEvent =
  | { type: "changed"; html: string }
  | { type: "saveStarted"; sentHtml: string }
  | { type: "saveSucceeded" }
  | { type: "saveFailed" }
  | { type: "serverContentChanged"; html: string; sameDraft: boolean; editing: boolean }
  | { type: "doneRequested" }
  | { type: "closed" };

export type DoneAction = "close" | "save-then-close" | "wait";

/**
 * How long the editor waits after the last change before it autosaves.
 * BUG-122 — 500 ms fired between the words of ordinary typing, a save per
 * word. This waits for a pause; Done still saves at once, and the
 * leave-page guard covers the unsaved window.
 */
export const AUTOSAVE_DELAY_MS = 1500;

/** Whether the debounced autosave should send the current text. */
export function shouldAutosave(state: DraftSaveState): boolean {
  return state.dirty && !state.saving && state.html !== state.failedHtml;
}

/** What pressing Done has to do first, given the current state. */
export function doneAction(state: DraftSaveState): DoneAction {
  if (state.saving) return "wait";
  return state.dirty ? "save-then-close" : "close";
}

export function initialDraftSaveState(html: string): DraftSaveState {
  return { baseline: html, html, dirty: false, saving: false, sentHtml: null, closeAfterSave: false, readyToClose: false, failedHtml: null };
}

export function draftSaveReducer(state: DraftSaveState, event: DraftSaveEvent): DraftSaveState {
  switch (event.type) {
    case "changed":
      return { ...state, html: event.html, dirty: event.html !== state.baseline };
    case "saveStarted":
      return { ...state, saving: true, sentHtml: event.sentHtml };
    case "saveSucceeded": {
      const baseline = state.sentHtml ?? state.baseline;
      const dirty = state.html !== baseline;
      const readyToClose = state.closeAfterSave && !dirty;
      return {
        ...state,
        baseline,
        dirty,
        saving: false,
        sentHtml: null,
        failedHtml: null,
        readyToClose,
        // Newer text still unsaved: keep waiting; it closes after that save.
        closeAfterSave: state.closeAfterSave && !readyToClose,
      };
    }
    case "saveFailed":
      return { ...state, saving: false, sentHtml: null, closeAfterSave: false, failedHtml: state.sentHtml };
    case "doneRequested":
      return doneAction(state) === "close"
        ? { ...state, readyToClose: true }
        : { ...state, closeAfterSave: true };
    case "closed":
      return { ...state, readyToClose: false, closeAfterSave: false };
    case "serverContentChanged":
      if (event.sameDraft && event.editing) return state;
      return initialDraftSaveState(event.html);
  }
}
